import { Link } from "react-router-dom";
import { LOGOS } from "../lib/brand";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "../components/ui/accordion";

const FAQS = [
  {
    q: "What is Musemi?",
    a: "Musemi is a curated shared wardrobe designed for the fashion-obsessed. Discover closets, rent full looks, and access incredible pieces for every version of your life — from nights out to weddings, vacations, events, and beyond.",
  },
  {
    q: "How does Musemi work?",
    a: "Browse curated closets and editorial edits, request pieces or full looks, wear them for your moment, then return them effortlessly. Musemi combines fashion discovery, community, and access into one seamless experience.",
  },
  {
    q: "What can I rent on Musemi?",
    a: "Musemi offers a curated mix of dresses, sets, outerwear, handbags, statement pieces, occasionwear, vacation looks, and elevated everyday styling. Inventory comes from both curated contributor closets and pieces sourced directly by Musemi.",
  },
  {
    q: "What is “Rent the Look”?",
    a: "Musemi goes beyond single-item rentals by offering complete styled looks curated for different occasions, destinations, and moods. Depending on availability, looks may include clothing, bags, jewelry, and accessories.",
  },
  {
    q: "Is Musemi membership-based?",
    a: "Musemi offers complimentary access to join the community, browse closets, save looks, and request rentals.\n\nAccess is complimentary. Trust is earned. Entry is curated.\n\nEvery application is reviewed to help maintain the quality, trust, and overall experience of the Musemi community.\n\nAn optional upgraded Muse Membership with additional perks and preferred pricing will also be available.",
  },
  {
    q: "What perks come with Muse Membership?",
    a: "Muse Membership is designed for members who want a more seamless and elevated experience. Perks may include complimentary shipping and returns, preferred rental pricing, early access to new drops, priority reservations, members-only edits, and access to future events and experiences.",
  },
  {
    q: "How does pricing work?",
    a: "Musemi uses curated pricing tiers designed to make luxury fashion feel more accessible and effortless. Pricing varies based on the category, rarity, designer, and demand of each piece.",
  },
  {
    q: "Who covers shipping?",
    a: "At launch, shipping costs will be calculated separately at checkout. Muse Membership will eventually include complimentary shipping and returns as part of the membership experience.",
  },
  {
    q: "Can I list my own pieces?",
    a: "Yes — Musemi works with a curated network of contributors who share exceptional pieces from their wardrobes. Every item is reviewed for brand fit, quality, condition, and overall aesthetic alignment before being accepted.",
  },
  {
    q: "How does contributing work?",
    a: "Approved contributors send or drop off their items directly to Musemi, where pieces are professionally stored, managed, photographed, and prepared for rentals.\n\nBy centrally managing inventory, Musemi is able to ensure quality control, seamless logistics, reliable delivery timelines, and a more elevated experience for both contributors and renters.",
  },
  {
    q: "Why does Musemi hold contributor inventory?",
    a: "Centralized inventory allows Musemi to maintain faster fulfillment, quality consistency, professional garment care, and a more seamless rental experience overall.\n\nThis approach helps create a luxury-service experience rather than a traditional peer-to-peer marketplace.",
  },
  {
    q: "Can contributors still access their items?",
    a: "Yes. Contributors can request the return of their items and manage availability directly through their item calendar and contributor dashboard.",
  },
  {
    q: "Who handles shipping and garment care?",
    a: "Musemi manages fulfillment, shipping, rental coordination, and garment care to create a seamless experience for both contributors and renters.",
  },
  {
    q: "What happens if an item is returned late?",
    a: "Musemi is building policies designed to protect both contributors and renters while keeping the experience seamless and fair. Additional details around late returns and fees will be shared closer to launch.",
  },
  {
    q: "What happens if an item is damaged?",
    a: "All rentals are reviewed upon return. Musemi takes preventative measures to protect contributor inventory and handles any damage or repair situations on a case-by-case basis in accordance with our policies.",
  },
  {
    q: "Where will Musemi be available?",
    a: "Musemi is launching first in Miami, with plans to expand into additional cities and destinations in the future.",
  },
  {
    q: "When is Musemi launching?",
    a: "We're currently building our founding community and carefully curating inventory ahead of launch. Join the waitlist for early access, updates, and future membership opportunities.",
  },
  {
    q: "Why Musemi?",
    a: "Because fashion should feel expressive, social, and ever-evolving — not worn once and forgotten. Musemi is about access over ownership, discovering new style identities, and participating in a more thoughtful fashion ecosystem.",
  },
];

export default function FAQ() {
  return (
    <div data-testid="faq-page" className="min-h-screen bg-[#F6F2EC] text-[#2B2320]">
      {/* Compact mauve masthead */}
      <header className="w-full" style={{ backgroundColor: "#634F50" }}>
        <div className="mx-auto max-w-[1480px] px-6 md:px-10 lg:px-14">
          {/* Top thin bar with back-to-home + waitlist */}
          <div
            className="flex items-center justify-between h-9 md:h-10 -mx-6 md:-mx-10 lg:-mx-14 px-6 md:px-10 lg:px-14"
            style={{ backgroundColor: "rgba(43, 35, 32, 0.18)" }}
          >
            <Link
              to="/"
              data-testid="faq-back-home"
              className="eyebrow text-[#F6F2EC] transition-opacity hover:opacity-60"
            >
              ← Back to Musemi
            </Link>
            <a
              href="https://tally.so/r/VLv7Ey"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="faq-waitlist-cta"
              className="eyebrow text-[#F6F2EC] transition-opacity hover:opacity-60 whitespace-nowrap"
            >
              Join the Waitlist
            </a>
          </div>

          {/* Wordmark */}
          <Link
            to="/"
            data-testid="faq-logo-link"
            aria-label="Musemi home"
            className="block"
          >
            <div className="mx-auto w-full max-w-[280px] sm:max-w-[340px] md:max-w-[380px] py-2 md:py-3">
              <div className="relative w-full" style={{ paddingBottom: "20%" }}>
                <img
                  src={LOGOS.cream}
                  alt="Musemi"
                  data-testid="faq-logo-img"
                  className="absolute inset-0 w-full h-full"
                  style={{ objectFit: "cover", objectPosition: "center" }}
                />
              </div>
            </div>
          </Link>
        </div>
      </header>

      {/* Hero / title */}
      <section className="w-full py-20 md:py-32">
        <div className="mx-auto max-w-[1080px] px-6 md:px-10 lg:px-14 text-center">
          <span className="eyebrow text-[#8A827D]">Frequently Asked</span>
          <h1
            data-testid="faq-heading"
            className="font-editorial text-[#2B2320] text-5xl md:text-6xl lg:text-7xl leading-[1.05] mt-8"
          >
            Questions, <span className="font-editorial-italic">answered.</span>
          </h1>
          <p className="mt-8 text-[#2B2320]/80 text-base md:text-lg font-light leading-relaxed max-w-xl mx-auto">
            Everything you'd like to know about renting, lending, and joining the Musemi community.
          </p>
        </div>
      </section>

      {/* Accordion */}
      <section className="w-full pb-28 md:pb-40">
        <div className="mx-auto max-w-[860px] px-6 md:px-10 lg:px-14">
          <Accordion
            type="single"
            collapsible
            className="w-full"
            data-testid="faq-accordion"
          >
            {FAQS.map((f, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                data-testid={`faq-item-${i}`}
                className="border-b border-[#2B2320]/15"
              >
                <AccordionTrigger
                  data-testid={`faq-trigger-${i}`}
                  className="font-editorial text-[#2B2320] text-xl md:text-2xl py-6 md:py-7 hover:no-underline [&_svg]:text-[#634F50] [&_svg]:h-5 [&_svg]:w-5"
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent
                  data-testid={`faq-content-${i}`}
                  className="text-[#2B2320]/85 text-base md:text-lg font-light leading-relaxed pb-8 md:pb-10 pr-6 md:pr-12 whitespace-pre-line"
                >
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Mauve waitlist closer */}
      <section
        className="w-full py-24 md:py-32"
        style={{ backgroundColor: "#634F50" }}
      >
        <div className="mx-auto max-w-[900px] px-6 md:px-10 lg:px-14 text-center">
          <h2 className="font-editorial text-[#F6F2EC] text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
            Still <span className="font-editorial-italic">curious?</span>
          </h2>
          <p className="mt-8 text-[#F6F2EC]/85 text-base md:text-lg font-light leading-relaxed max-w-md mx-auto">
            Join the waitlist and we'll be in touch with previews, fittings, and your invitation to the wardrobe.
          </p>
          <a
            href="https://tally.so/r/VLv7Ey"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="faq-bottom-cta"
            className="eyebrow text-[#F6F2EC] border-b border-[#F6F2EC] pb-2 hover:opacity-60 transition-opacity inline-block mt-12"
          >
            Join the Waitlist
          </a>
        </div>
      </section>

      {/* Minimal footer */}
      <footer className="w-full bg-[#F6F2EC] py-16 text-center">
        <Link to="/" className="eyebrow text-[#8A827D] hover:text-[#2B2320] transition-colors">
          Back to Musemi
        </Link>
      </footer>
    </div>
  );
}
